import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetProjectQuery, useGetVersionScenarioConfigurationQuery, useGetVersionsQuery } from "fond/api";
import { Maybe } from "fond/types";
import { isCollaborationProject } from "fond/utils/project";

interface UseProjectHasScenarioConfigurationProps {
  projectId: Maybe<string>;
}

interface UseProjectHasScenarioConfigurationReturn {
  hasConfig: boolean;
  isLoading: boolean;
}

/**
 * Return whether the given project has a scenario configuration.
 *
 * We say that a project has a scenario configuration if its root version has a scenario configuration.
 */
export const useProjectHasScenarioConfiguration = ({
  projectId,
}: UseProjectHasScenarioConfigurationProps): UseProjectHasScenarioConfigurationReturn => {
  const { currentData: project, isLoading: isLoadingProject } = useGetProjectQuery(projectId || skipToken);

  // Scenarios are only supported on collaboration projects, so skip all the other queries if this isn't the case.
  const isCollaboration = isCollaborationProject(project);
  const { currentData: versions, isLoading: isLoadingVersions } = useGetVersionsQuery(projectId || skipToken, { skip: !isCollaboration });
  const { currentData: configuration, isLoading: isLoadingConfiguration } = useGetVersionScenarioConfigurationQuery(
    // only the root (first) version in a collaboration project can have a scenario configuration. versions
    // are ordered from newest to oldest, so the root version is the last one in the list.
    versions && versions.ids?.length > 0 ? (versions.ids.at(-1) as string) : skipToken,
    { skip: !isCollaboration }
  );
  const isLoading = isLoadingProject || isLoadingVersions || isLoadingConfiguration;

  return useMemo(
    () => ({
      hasConfig: !!configuration?.ID,
      isLoading,
    }),
    [configuration?.ID, isLoading]
  );
};

export default useProjectHasScenarioConfiguration;
